// Learn (contracts/learn.md): stories (swipeable carousels of slides, published now or on a schedule with a
// lesson push) and the daily brain card. Every role reads; editors write. Kept apart from client.ts /
// queries.ts like the other feature modules.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { request } from "./client";
import { APP_LINKS, type AppLink, ImageRef, LOCALES } from "./messaging";

const count = z.number().int().min(0);
/** Per locale, the fields it has (English required; the rest fall back field by field). */
const localized = <T extends z.ZodType>(entry: T) => z.partialRecord(z.enum(LOCALES), entry);

// ---------------------------------------------------------------- stories

export const PUZZLE_CATEGORIES = ["pattern", "number", "spatial", "logic", "memory", "speed", "planning", "word"] as const;
export const STORY_CATEGORIES = [...PUZZLE_CATEGORIES, "general"] as const;
export type StoryCategory = (typeof STORY_CATEGORIES)[number];
export const STORY_STATUSES = ["draft", "scheduled", "published", "archived"] as const;
export type StoryStatus = (typeof STORY_STATUSES)[number];
export const SLIDE_LAYOUTS = ["image", "image_text", "big_stat", "tip", "steps", "quote"] as const;
export type SlideLayout = (typeof SLIDE_LAYOUTS)[number];
export const CTA_ACTIONS = ["none", "practice", "link"] as const;
export type CtaAction = (typeof CTA_ACTIONS)[number];

/** Story text limits (contracts/learn.md §1 "Model"). */
export const STORY_LIMITS = { title: 60, subtitle: 120, cta: 30 } as const;
export const MAX_SLIDES = 12;
export const MAX_PUZZLE_TYPES = 10;
/** `category.slug`, as the API checks puzzle types. */
export const PUZZLE_TYPE_RE = /^[a-z]+\.[a-z0-9_]+$/;

export const StoryText = z.object({ title: z.string().optional(), subtitle: z.string().optional(), cta: z.string().optional() });
export const SlideText = z.object({
  headline: z.string().optional(),
  text: z.string().optional(),
  stat: z.string().optional(),
  steps: z.array(z.string()).optional(),
  source: z.string().optional(),
});
export type SlideText = z.infer<typeof SlideText>;
export const StoryContent = localized(StoryText);
export type StoryContent = z.infer<typeof StoryContent>;
export const SlideContent = localized(SlideText);
export type SlideContent = z.infer<typeof SlideContent>;

export const AdminSlide = z.object({ id: z.string(), layout: z.enum(SLIDE_LAYOUTS), image: ImageRef.nullable(), content: SlideContent });
export type AdminSlide = z.infer<typeof AdminSlide>;

export const AdminStory = z.object({
  id: z.string(),
  status: z.enum(STORY_STATUSES),
  publishAt: z.string().nullable(),
  publishedAt: z.string().nullable(),
  category: z.enum(STORY_CATEGORIES),
  puzzleTypes: z.array(z.string()),
  cover: ImageRef.nullable(),
  content: StoryContent,
  slides: z.array(AdminSlide),
  ctaAction: z.enum(CTA_ACTIONS),
  link: z.enum(APP_LINKS).nullable(),
  push: z.boolean(),
  /** What blocks publishing (empty when it can go out). */
  problems: z.array(z.string()),
  /** Unique players; `pushed`: players the lesson push went to. */
  stats: z.object({ views: count, completed: count, completionRate: z.number().min(0).nullable(), avgSlide: z.number().min(0).nullable(), pushed: count }),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AdminStory = z.infer<typeof AdminStory>;
export const AdminStories = z.object({ items: z.array(AdminStory) });

export interface SlideDraft {
  /** Kept across edits; the API generates one for a new slide. */
  id?: string;
  layout: SlideLayout;
  imageId: string | null;
  content: SlideContent;
}

/** POST /stories (PATCH takes any subset; `status` changes only through publish and archive). */
export interface StoryDraft {
  category: StoryCategory;
  puzzleTypes: string[];
  coverImageId: string | null;
  content: StoryContent;
  slides: SlideDraft[];
  ctaAction: CtaAction;
  link: AppLink | null;
  push: boolean;
}

export const storiesApi = {
  list: (status?: StoryStatus, signal?: AbortSignal) => request(AdminStories, "GET", "/stories", { query: { status }, signal }),
  get: (id: string, signal?: AbortSignal) => request(AdminStory, "GET", `/stories/${encodeURIComponent(id)}`, { signal }),
  create: (draft: StoryDraft) => request(AdminStory, "POST", "/stories", { body: draft }),
  update: (id: string, patch: Partial<StoryDraft>) => request(AdminStory, "PATCH", `/stories/${encodeURIComponent(id)}`, { body: patch }),
  /** Now, or scheduled with `at` (ISO, in the future). */
  publish: (id: string, at?: string) => request(AdminStory, "POST", `/stories/${encodeURIComponent(id)}/publish`, { body: at ? { at } : {} }),
  archive: (id: string) => request(z.null(), "POST", `/stories/${encodeURIComponent(id)}/archive`),
  remove: (id: string) => request(z.null(), "DELETE", `/stories/${encodeURIComponent(id)}`),
};

export const storyKeys = { all: ["stories"] as const, list: () => ["stories", "list"] as const, one: (id: string) => ["stories", "one", id] as const };

export const useStories = () => useQuery({ queryKey: storyKeys.list(), queryFn: ({ signal }) => storiesApi.list(undefined, signal) });
export const useStory = (id: string | undefined) =>
  useQuery({ queryKey: storyKeys.one(id ?? ""), queryFn: ({ signal }) => storiesApi.get(id!, signal), enabled: Boolean(id) });

function useLearnMutation<V, R>(fn: (v: V) => Promise<R>, root: readonly string[]) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: root });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export const useCreateStory = () => useLearnMutation((draft: StoryDraft) => storiesApi.create(draft), storyKeys.all);
export const useUpdateStory = () => useLearnMutation(({ id, patch }: { id: string; patch: Partial<StoryDraft> }) => storiesApi.update(id, patch), storyKeys.all);
export const usePublishStory = () => useLearnMutation(({ id, at }: { id: string; at?: string }) => storiesApi.publish(id, at), storyKeys.all);
export const useArchiveStory = () => useLearnMutation((id: string) => storiesApi.archive(id), storyKeys.all);
export const useDeleteStory = () => useLearnMutation((id: string) => storiesApi.remove(id), storyKeys.all);

export const CATEGORY_LABEL: Record<StoryCategory, string> = {
  pattern: "Pattern",
  number: "Number",
  spatial: "Spatial",
  logic: "Logic",
  memory: "Memory",
  speed: "Speed",
  planning: "Planning",
  word: "Word",
  general: "General",
};
export const STATUS_TONE: Record<StoryStatus, string> = { draft: "neutral", scheduled: "info", published: "success", archived: "muted" };
export const CTA_LABEL: Record<CtaAction, string> = { none: "No button", practice: "Practise (Focus practice)", link: "Open a place in the app" };

// ---------------------------------------------------------------- brain cards

export const BRAIN_CATEGORIES = ["brain", "reasoning", "everyday", "habit"] as const;
export type BrainCategory = (typeof BRAIN_CATEGORIES)[number];
export const BRAIN_STATUSES = ["draft", "ready"] as const;
export type BrainStatus = (typeof BRAIN_STATUSES)[number];
/** Brain card text limits (contracts/learn.md §2 "Model"). */
export const BRAIN_LIMITS = { title: 60, text: 280, more: 1200, source: 200 } as const;

export const BrainText = z.object({ title: z.string().optional(), text: z.string().optional(), more: z.string().optional(), source: z.string().optional() });
export const BrainContent = localized(BrainText);
export type BrainContent = z.infer<typeof BrainContent>;

export const AdminBrainCard = z.object({
  id: z.string(),
  /** YYYY-MM-DD, or null when not scheduled. */
  date: z.string().nullable(),
  evergreen: z.boolean(),
  category: z.enum(BRAIN_CATEGORIES),
  content: BrainContent,
  image: ImageRef.nullable(),
  status: z.enum(BRAIN_STATUSES),
  /** Unique players who opened it. */
  opens: count,
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AdminBrainCard = z.infer<typeof AdminBrainCard>;
/** `evergreen`: the date shows a card from the rotation (nothing scheduled for it). */
export const BrainCalendarDay = z.object({ date: z.string(), cardId: z.string().nullable(), evergreen: z.boolean() });
export type BrainCalendarDay = z.infer<typeof BrainCalendarDay>;
export const AdminBrainCards = z.object({ items: z.array(AdminBrainCard), calendar: z.array(BrainCalendarDay) });

export interface BrainCardDraft {
  date: string | null;
  evergreen: boolean;
  category: BrainCategory;
  content: BrainContent;
  imageId: string | null;
  status: BrainStatus;
}

export const brainApi = {
  /** The calendar defaults to today − 7 … today + 30 (UTC dates). */
  list: (range?: { from: string; to: string }, signal?: AbortSignal) => request(AdminBrainCards, "GET", "/brain-cards", { query: range, signal }),
  create: (draft: BrainCardDraft) => request(AdminBrainCard, "POST", "/brain-cards", { body: draft }),
  update: (id: string, patch: Partial<BrainCardDraft>) => request(AdminBrainCard, "PATCH", `/brain-cards/${encodeURIComponent(id)}`, { body: patch }),
  remove: (id: string) => request(z.null(), "DELETE", `/brain-cards/${encodeURIComponent(id)}`),
};

export const brainKeys = { all: ["brain"] as const, list: () => ["brain", "list"] as const };

export const useBrainCards = () => useQuery({ queryKey: brainKeys.list(), queryFn: ({ signal }) => brainApi.list(undefined, signal) });
export const useCreateBrainCard = () => useLearnMutation((draft: BrainCardDraft) => brainApi.create(draft), brainKeys.all);
export const useUpdateBrainCard = () => useLearnMutation(({ id, patch }: { id: string; patch: Partial<BrainCardDraft> }) => brainApi.update(id, patch), brainKeys.all);
export const useDeleteBrainCard = () => useLearnMutation((id: string) => brainApi.remove(id), brainKeys.all);

export const BRAIN_CATEGORY_LABEL: Record<BrainCategory, string> = { brain: "Brain", reasoning: "Reasoning", everyday: "Everyday", habit: "Habit" };
export const BRAIN_CATEGORY_HINT: Record<BrainCategory, string> = {
  brain: "How the brain works.",
  reasoning: "How reasoning and intelligence research work.",
  everyday: "Using it in real life.",
  habit: "Practice that helps. The only category that may go without a source.",
};
export const BRAIN_CATEGORY_TONE: Record<BrainCategory, string> = { brain: "violet", reasoning: "info", everyday: "teams", habit: "arena" };
